import styled from "@emotion/styled"
import { PButton, PButtonPure, PInlineNotification, PText } from "@porsche-design-system/components-react"
import { spacingFluidXs, spacingFluidSm, spacingFluidMd } from "@porsche-design-system/components-react/emotion"
import type { CapCustomer } from "@slfinrtl/ui-bff-routes"
import { FormattedMessage, useIntl } from "react-intl"
import { ButtonGroup, Skeleton } from "src/components"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { CustomerInfo } from "src/screens/offers/common/components/CustomerInfo"
import { offerMessages } from "src/screens/offers/common/offer.messages"

interface TransferVehicleModalContentProps {
	customer: CapCustomer | undefined
	hasCapError: boolean
	hasVehicleData: boolean
	hasVehicleDataError: boolean
	isCapInitialized: boolean
	isCustomerNameLoading: boolean
	isTransferring: boolean
	onCancel: () => void
	onCapErrorDismiss: () => void
	onConfirmTransfer: () => void
	selectCustomerFromCap: () => void
}

export const TransferVehicleModalContent = ({
	customer,
	hasCapError,
	hasVehicleData,
	hasVehicleDataError,
	isCapInitialized,
	isCustomerNameLoading,
	isTransferring,
	onCancel,
	onCapErrorDismiss,
	onConfirmTransfer,
	selectCustomerFromCap,
}: TransferVehicleModalContentProps) => {
	const { formatMessage } = useIntl()

	return (
		<>
			<StyledModalContent>
				<PText>
					<FormattedMessage {...offerMessages.transferVehicleModalDescription} />
				</PText>

				{hasCapError || hasVehicleDataError ? (
					<Notification
						data-testid="cap-warning"
						state="warning"
						heading={formatMessage(offerMessages.capWarningHeading)}
						description={formatMessage(offerMessages.capWarningDescription)}
						onDismiss={onCapErrorDismiss}
					/>
				) : (
					<ErrorNotificationInline dismissButton={false} />
				)}

				<CustomerSection>
					{isCustomerNameLoading ? (
						<SkeletonStack>
							<Skeleton.Heading size="sm" />
							<Skeleton.Text $width="80%" />
							<Skeleton.Text $width="80%" />
						</SkeletonStack>
					) : customer ? (
						<>
							<CustomerInfo customer={customer} />
							<PButtonPure
								data-testid="edit-crm-customer-button"
								icon="edit"
								onClick={selectCustomerFromCap}
							>
								<FormattedMessage {...offerMessages.customerEditCapCustomer} />
							</PButtonPure>
						</>
					) : isCapInitialized && hasVehicleData ? (
						<div>
							<PButtonPure
								icon="plus"
								onClick={selectCustomerFromCap}
								data-testid="select-customer-from-cap-button"
							>
								<FormattedMessage {...offerMessages.transferVehicleSelectCustomerButton} />
							</PButtonPure>
						</div>
					) : (
						<Skeleton.Text $width="220px" />
					)}
				</CustomerSection>
			</StyledModalContent>

			<ButtonGroup>
				<PButton
					onClick={onConfirmTransfer}
					loading={isTransferring}
					disabled={!customer}
					data-testid="transfer-vehicle-modal-confirm-button"
				>
					<FormattedMessage {...offerMessages.transferVehicleConfirmButton} />
				</PButton>
				<PButton
					type="button"
					variant="secondary"
					onClick={onCancel}
					data-testid="transfer-vehicle-modal-cancel-button"
				>
					<FormattedMessage {...offerMessages.transferVehicleCancelButton} />
				</PButton>
			</ButtonGroup>
		</>
	)
}

const StyledModalContent = styled.div`
	max-width: 40rem;
	display: flex;
	flex-direction: column;
	gap: ${spacingFluidMd};
`

const CustomerSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingFluidSm};
`

const SkeletonStack = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingFluidXs};
`

const Notification = styled(PInlineNotification)`
	max-width: 100%;
`
